/**
 * ⟦A-5⟧ « Chaque déverrouillage est **un moment de jeu**, jamais un silence. »
 *
 * Ce composant est la traduction littérale de cette phrase de l'avenant : dès qu'un étage de
 * l'escalier s'ouvre, le joueur reçoit un écran qui le lui dit. Il ne peut pas le rater, et il
 * ne le reverra pas au F5 suivant (`unlocksSeen` vit dans la run).
 *
 * ⟦A-9⟧ **Sauf quand le mode découverte prend le relais.** Les étages ont chacun leur leçon, et
 * cette leçon s'ouvre déjà sur l'annonce de l'escalier (`paragraphes()`). Afficher les deux, ce
 * serait dire deux fois la même chose à la suite — et transformer une ouverture en corvée. Dans
 * ce cas on consomme le déverrouillage en silence : ce n'en est pas un, la leçon le raconte.
 */

import { useEffect } from "react";

import { leconDeLEtage } from "../game/decouverte";
import { etage } from "../game/escalier";
import { useDecouverteStore } from "../stores/decouverteStore";
import { useRunStore } from "../stores/runStore";
import { dialog, useUIStore } from "../stores/uiStore";

export function MomentsDebloques() {
  const file = useRunStore((s) => s.deverrouillagesEnAttente);
  const consommer = useRunStore((s) => s.consommerDeverrouillage);
  // Un palier est le plus gros moment des trois : il passe devant (⟦A-4 #13⟧).
  const paliers = useRunStore((s) => s.paliersEnAttente.length);
  const chute = useRunStore((s) => s.chute);
  const decouverteActive = useUIStore((s) => s.settings.decouverte);
  const leconEnCours = useDecouverteStore((s) => s.courante) !== null;
  const dejaVue = useDecouverteStore((s) => s.dejaVue);
  // ⟦A-16⟧ Le récapitulatif des jours écoulés passe avant l'annonce d'ouverture, comme le palier.
  const recap = useRunStore((s) => s.recapEnAttente) !== null;
  const suspendus =
    useUIStore((s) => s.momentsSuspendus) ||
    paliers > 0 ||
    chute !== null ||
    leconEnCours ||
    recap;
  const prochain = file[0];

  useEffect(() => {
    if (!prochain || suspendus) return;

    // Le mode découverte va-t-il raconter cette ouverture lui-même ? Alors on s'efface.
    const lecon = leconDeLEtage(prochain);
    if (decouverteActive && lecon && !dejaVue(lecon.id)) {
      consommer();
      return;
    }

    const e = etage(prochain);
    let vivant = true;
    // ⟦C57 · lot 3 · A-174⟧ Le nom de l'étage (5 mots au plus) et ce qu'il ouvre en UNE phrase. L'annonce
    // d'avant (`e.annonce`, intacte) rejoint la ligne d'ouverture du Journal, qui la rouvre par « Les
    // dessous » (`game/journal.ts`, `ligneDeverrouillage`) : ce dialogue-ci reste un `dialog.info` du store
    // (qui ne porte qu'un texte), parce que `ModeDecouverte`, `InfoBulleDecouverte` et `useMattChambre`
    // attendent `uiStore.dialog` pour ne pas parler par-dessus une ouverture.
    void dialog
      .info({
        icone: e.icone,
        title: e.titre_court ?? e.titre,
        body: e.annonce_courte,
        confirmLabel: "Voir ça",
      })
      .then(() => {
        if (vivant) consommer();
      });
    return () => {
      vivant = false;
    };
  }, [prochain, suspendus, consommer, decouverteActive, dejaVue]);

  return null;
}
